import { action, computed, makeObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { DateFormat, formatDateFromString, formatDateToString } from 'us-react-components/dist/utils/date';
import {
  Include,
  Job,
  JobAvailabilityType,
  JobType,
  Parent,
  RecurringDay,
  RecurringDetails,
  Sweetener,
} from 'us-web-services';
import { Routes } from '../../Routes';
import { ParentStore } from '../ParentStore';
import { UserStore } from '../UserStore';
import { flowSteps, FlowType } from './constants';
import { Storage } from '../../constants';

interface DateInput {
  id: number;
  value: string;
}

export interface LabeledId {
  id?: number;
  label?: string;
}

const ageSort = (a: DateInput, b: DateInput): number => {
  const [monthA, yearA] = a.value.split('/');
  const [monthB, yearB] = b.value.split('/');
  const monthIntA = parseInt(monthA, 10);
  const yearIntA = parseInt(yearA, 10);
  const monthIntB = parseInt(monthB, 10);
  const yearIntB = parseInt(yearB, 10);

  return yearIntB - yearIntA || monthIntB - monthIntA;
}

const ageString = (input: DateInput): string => {
  if (!input.value) {
    return '';
  }

  const [birthMonthStr, birthYearStr] = input.value.split('/');
  const birthMonthNum = parseInt(birthMonthStr, 10);
  const birthYear = parseInt(birthYearStr, 10);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // January is 0, so add 1 to get 1-12 range

  if (currentYear < birthYear || (currentYear === birthYear && currentMonth < birthMonthNum)) {
    return 'Arriving soon';
  }

  const ageInMonths = Math.max((currentYear - birthYear) * 12 + currentMonth - birthMonthNum, 1);
  const ageInYears = Math.floor(ageInMonths / 12);

  if (ageInMonths < 24) {
    return `${ageInMonths} month${ageInMonths === 1 ? '' : 's'}`;
  }

  return `${ageInYears} year${ageInYears === 1 ? '' : 's'}`;
};

export class FamilyRegistrationStore {
  currentRoute = Routes.parent.registration.gettingStarted;
  lookingForId?: number;
  justBrowsing: boolean = false;
  profile: Parent = {
    address: {
      city: '',
      neighborhood: '',
      state: '',
      zip: '',
    },
  };
  availabilityType?: JobAvailabilityType;
  jobType?: JobType;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  startTime?: string;
  endTime?: string;
  includes: Include[] = [];
  sweeteners: Sweetener[] = [];
  countDependents: number = 1;
  childrenAges: DateInput[] = [{ id: 0, value: '' }];
  details: string = '';
  recurring?: RecurringDetails;
  savedSearchId?: number;
  signupVerticalTypeId?: number;
  jobId?: number;
  createdOn?: Date;
  dog?: boolean;
  cat?: boolean;
  otherPet?: boolean;
  languages: LabeledId[] = [];
  teachingSkills: LabeledId[] = [];
  specialSituations: LabeledId[] = [];
  specialServices: LabeledId[] = [];
  specialExperiences: LabeledId[] = [];

  constructor(userStore: UserStore, parentStore: ParentStore) {
    this.profile = parentStore.parent ?? {
      id: userStore.user.id,
      address: {
        city: '',
        neighborhood: '',
        state: '',
        zip: '',
      },
    };

    makeObservable(this, {
      // Observables
      availabilityType: observable,
      childrenAges: observable,
      countDependents: observable,
      currentRoute: observable,
      details: observable,
      endDate: observable,
      endTime: observable,
      includes: observable,
      jobType: observable,
      justBrowsing: observable,
      lookingForId: observable,
      recurring: observable,
      startDate: observable,
      startTime: observable,
      sweeteners: observable,
      savedSearchId: observable,
      signupVerticalTypeId: observable,
      jobId: observable,
      createdOn: observable,
      dog: observable,
      cat: observable,
      otherPet: observable,
      languages: observable,
      teachingSkills: observable,
      specialSituations: observable,
      specialServices: observable,
      specialExperiences: observable,

      // Actions
      setAvailabilityType: action,
      setChildrenAges: action,
      setCity: action,
      setCountDependents: action,
      setCurrentRoute: action,
      setDetails: action,
      setEndDate: action,
      setEndTime: action,
      setIncludes: action,
      setNeighborhood: action,
      setJobType: action,
      setJustBrowsing: action,
      setLookingFor: action,
      setRecurring: action,
      setStartDate: action,
      setStartTime: action,
      setState: action,
      setSweeteners: action,
      setZipCode: action,
      setSavedSearchId: action,
      setSignupVerticalTypeId: action,
      setJobId: action,
      setCreatedOn: action,
      setDog: action,
      setCat: action,
      setOtherPet: action,
      setLanguages: action,
      setTeachingSkills: action,
      setSpecialSituations: action,
      setSpecialServices: action,
      setSpecialExperiences: action,

      // Computeds
      childrenAgesString: computed,
      currentStep: computed,
      daysOfTheWeekString: computed,
      endString: computed,
      endTimeDisplay: computed,
      flowType: computed,
      job: computed,
      startString: computed,
      startTimeDisplay: computed,
      totalSteps: computed,
    });

    makePersistable(this,
      {
        name: Storage.FAMILY_REGISTRATION_STORE,
        properties: [
          'currentRoute',
          'lookingForId',
          'justBrowsing',
          'availabilityType',
          'jobType',
          {
            key: 'startDate',
            serialize: (value: Date | undefined): Date | undefined => value,
            deserialize: (value: string | undefined): Date | undefined => value ? new Date(value) : undefined,
          },
          {
            key: 'endDate',
            serialize: (value: Date | undefined): Date | undefined => value,
            deserialize: (value: string | undefined): Date | undefined => value ? new Date(value) : undefined,
          },
          'startTime',
          'endTime',
          'includes',
          'sweeteners',
          'countDependents',
          'childrenAges',
          'details',
          'recurring',
          'savedSearchId',
          'signupVerticalTypeId',
          'savedSearchId',
          'jobId',
          'createdOn',
          'dog',
          'cat',
          'otherPet',
          'languages',
          'teachingSkills',
          'specialSituations',
          'specialServices',
          'specialExperiences'
        ],
        storage: window.localStorage
      });
  }


  clearData(): void {
    localStorage.removeItem(Storage.UUID);
    this.resetDependents();
    this.setStartDate(undefined);
    this.setEndDate(undefined);
    this.setStartTime('');
    this.setEndTime('');
    this.setAvailabilityType(undefined);
    this.setSweeteners([]);
    this.setIncludes([]);
    this.setJustBrowsing(false);
    this.setRecurring(undefined);
    this.setLookingFor(undefined);
    this.setJobType(undefined);
    this.setDetails('');
    this.setCurrentRoute('');
    this.setSavedSearchId(undefined);
    this.setSignupVerticalTypeId(undefined);
    this.setJobId(undefined);
    this.setCreatedOn(undefined);
    this.setDog(undefined);
    this.setCat(undefined);
    this.setOtherPet(undefined);
    this.setLanguages([]);
    this.setTeachingSkills([]);
    this.setSpecialSituations([]);
    this.setSpecialServices([]);
    this.setSpecialExperiences([]);
  }

  get flowType(): FlowType {
    if (this.justBrowsing) {
      switch (this.lookingForId) {
        case (bookingVerticalType.PET_CARE):
          return FlowType.PetCareBrowsing;
        default:
          return FlowType.OtherBrowsing;
      }
    }

    switch (this.lookingForId) {
      case (bookingVerticalType.PET_CARE):
        return FlowType.PetCareRecurring;
      case bookingVerticalType.SENIOR_CARE:
        return FlowType.SeniorCare;
      case bookingVerticalType.HOUSEHOLD_SERVICES:
        return FlowType.HouseholdServices;
      case bookingVerticalType.CHILD_CARE:
      default:
        return FlowType.ChildCare;
    }
  }

  get totalSteps(): number {
    return flowSteps[this.flowType]?.length;
  }

  get currentStep(): number {
    const steps = flowSteps[this.flowType];
    const index = steps.indexOf(this.currentRoute);

    return index !== -1 ? index + 1 : 0;
  }

  browsingOrAbandoned(): boolean {
    return this.justBrowsing || !this.availabilityType;
  }

  resetDependents(): void {
    this.setCountDependents(1);
    this.setChildrenAges([{ id: 0, value: '' }]);
  }

  setCurrentRoute(currentRoute: string): void {
    this.currentRoute = currentRoute;
  }

  setJustBrowsing(justBrowsing: boolean): void {
    this.justBrowsing = justBrowsing;
  }

  setAvailabilityType(availabilityType?: JobAvailabilityType): void {
    this.availabilityType = availabilityType;
  }

  setLookingFor(lookingForId?: number): void {
    this.lookingForId = lookingForId;
  }

  setJobType(jobType?: JobType): void {
    this.jobType = jobType;
  }

  setStartDate(startDate: Date | undefined): void {
    this.startDate = startDate;
  }

  setEndDate(endDate: Date | undefined): void {
    this.endDate = endDate;
  }

  setStartTime(startTime: string): void {
    this.startTime = startTime;
  }

  setEndTime(endTime: string): void {
    this.endTime = endTime;
  }

  setCountDependents(countDependents: number): void {
    this.countDependents = countDependents;
  }

  setChildrenAges(childrenAges: DateInput[]): void {
    this.childrenAges = childrenAges;
  }

  setCity(city: string): void {
    if (!this.profile.address) {
      this.profile.address = {
        city,
        neighborhood: '',
        state: '',
        zip: '',
      };
    } else {
      this.profile.address.city = city;
    }
  }

  setNeighborhood(neighborhood: string): void {
    if (!this.profile.address) {
      this.profile.address = {
        city: '',
        neighborhood,
        state: '',
        zip: '',
      };
    } else {
      this.profile.address.neighborhood = neighborhood;
    }
  }

  setState(state: string): void {
    if (!this.profile.address) {
      this.profile.address = {
        city: '',
        neighborhood: '',
        state,
        zip: '',
      };
    } else {
      this.profile.address.state = state;
    }
  }

  setZipCode(zip: string): void {
    if (!this.profile.address) {
      this.profile.address = {
        city: '',
        neighborhood: '',
        state: '',
        zip,
      };
    } else {
      this.profile.address.zip = zip;
    }
  }

  setIncludes(includes: Include[]): void {
    this.includes = includes;
  }

  setSweeteners(sweeteners: Sweetener[]): void {
    this.sweeteners = sweeteners;
  }

  setDetails(details: string): void {
    this.details = details;
  }

  setRecurring(recurring?: RecurringDetails): void {
    if (recurring?.days?.length) {
      recurring.days.sort((a: RecurringDay, b: RecurringDay) => a.weekday.id - b.weekday.id);
    }

    this.recurring = recurring;
  }

  setSavedSearchId(savedSearchId?: number): void {
    this.savedSearchId = savedSearchId;
  }

  setSignupVerticalTypeId(signupVerticalTypeId?: number): void {
    this.signupVerticalTypeId = signupVerticalTypeId;
  }

  setJobId(jobId?: number): void {
    this.jobId = jobId;
  }

  setCreatedOn(createdOn?: Date): void {
    this.createdOn = createdOn;
  }

  setDog(dog?: boolean): void {
    this.dog = dog;
  }

  setCat(cat?: boolean): void {
    this.cat = cat;
  }

  setOtherPet(otherPet?: boolean): void {
    this.otherPet = otherPet;
  }

  setLanguages(languages: LabeledId[]): void {
    this.languages = languages;
  }

  setTeachingSkills(teachingSkills: LabeledId[]): void {
    this.teachingSkills = teachingSkills;
  }

  setSpecialSituations(specialSituations: LabeledId[]): void {
    this.specialSituations = specialSituations;
  }

  setSpecialServices(specialServices: LabeledId[]): void {
    this.specialServices = specialServices;
  }

  setSpecialExperiences(specialExperiences: LabeledId[]): void {
    this.specialExperiences = specialExperiences;
  }

  get job(): Job {
    return {
      status: 'new',
      booking: {
        parent: {
          id: this.profile.id,
        },
        address: {
          city: this.profile.address?.city ?? '',
          neighborhood: this.profile.address?.neighborhood ?? '',
          state: this.profile.address?.state ?? '',
          street: '',
          street2: '',
          zip: this.profile.address?.zip ?? '',
        },
        kidsAges: this.childrenAgesString,
        numberOfChildren: this.countDependents,
        paymentType: 'cash',
        typeId: this.lookingForId,
        start: this.startString,
        end: this.endString,
        description: this.details,
        includes: this.includes,
        sweeteners: this.sweeteners,
      },
      availability: this.availabilityType,
      recurring: this.recurring,
      type: this.jobType,
      id: undefined,
    }
  }

  get childrenAgesString(): string {
    return this.childrenAges.length ? `(${this.childrenAges.slice().sort(ageSort).map((input) => ageString(input)).join(', ')})` : '';
  }

  get startString(): string | undefined {
    if (!this.startDate) {
      return undefined;
    }

    if (!this.startTime) {
      return formatDateToString(this.startDate!, DateFormat.API_DATE_TIME_FORMAT);
    }

    return `${formatDateToString(this.startDate!, DateFormat.API_DATE_FORMAT)} ${this.startTime}`;
  }

  get startTimeDisplay(): string {
    return (this.startTime && this.startString) ? formatDateFromString(this.startString, DateFormat.TIME_DISPLAY_FORMAT) : '';
  }

  get endString(): string | undefined {
    if (this.endDate) {
      if (!this.endTime) {
        return formatDateToString(this.endDate!, DateFormat.API_DATE_TIME_FORMAT);
      }

      return `${formatDateToString(this.endDate!, DateFormat.API_DATE_FORMAT)} ${this.endTime}`;
    }

    if (this.availabilityType === JobAvailabilityType.occasional) {
      if (!this.startDate) {
        return undefined;
      }

      return `${formatDateToString(this.startDate, DateFormat.API_DATE_FORMAT)} ${this.endTime}`;
    }

    return undefined;
  }

  get endTimeDisplay(): string {
    return (this.endTime && this.endString) ? formatDateFromString(this.endString, DateFormat.TIME_DISPLAY_FORMAT) : '';
  }

  get daysOfTheWeekString(): string {
    if (!this.recurring?.days) {
      return '';
    }

    return `${this.recurring.days.map((day: { weekday: { code: string; }; }) => day.weekday.code).join(', ')}`;
  }
}
